import React from 'react';
import { Button, Layout, Menu, Tag, Typography } from 'antd';
import {
  AppstoreOutlined,
  BarChartOutlined,
  BookOutlined,
  DollarOutlined,
  FundOutlined,
  LogoutOutlined,
  MailOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { EchoLogo } from './Echo';
import { EnvBadge } from './EnvBadge';
import type { Route } from '../router';

const { Sider, Header, Content } = Layout;

export const LATER_SECTIONS = [
  { key: 'statistics', label: 'Statistics', icon: <BarChartOutlined /> },
] as const;

/** Screens with their own address, in the order of the menu (the placeholders follow). */
const LIVE_SECTIONS = [
  { key: 'decks', label: 'Public decks', icon: <AppstoreOutlined /> },
  { key: 'cards', label: 'Cards', icon: <BookOutlined /> },
  { key: 'users', label: 'Users', icon: <TeamOutlined /> },
  { key: 'revenue', label: 'Revenue', icon: <FundOutlined /> },
  { key: 'finance', label: 'Finance', icon: <DollarOutlined /> },
  { key: 'emails', label: 'Emails', icon: <MailOutlined /> },
] as const;
type LiveKey = (typeof LIVE_SECTIONS)[number]['key'];

function selectedKey(route: Route): string {
  switch (route.name) {
    case 'soon':
      return route.section;
    case 'user':
      return 'users';
    case 'deck':
      return 'decks';
    default:
      return route.name;
  }
}

const WIDE_ROUTES: Array<Route['name']> = ['cards', 'users', 'finance', 'emails'];

export const Shell: React.FC<{
  route: Route;
  navigate: (route: Route) => void;
  email: string;
  onSignOut: () => void;
  children: React.ReactNode;
}> = ({ route, navigate, email, onSignOut, children }) => (
  <Layout style={{ minHeight: '100vh' }}>
    <Sider breakpoint="lg" collapsedWidth={0} width={220}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '18px 20px 14px',
          color: '#fff',
        }}
      >
        <EchoLogo size={34} />
        <div style={{ lineHeight: 1.2 }}>
          <div style={{ fontWeight: 700, fontSize: 16 }}>LingoPing</div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>Admin</div>
        </div>
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[selectedKey(route)]}
        onClick={({ key }) =>
          LIVE_SECTIONS.some((s) => s.key === key)
            ? navigate({ name: key as LiveKey })
            : navigate({ name: 'soon', section: key })
        }
        items={[
          ...LIVE_SECTIONS.map((s) => ({ key: s.key, icon: s.icon, label: s.label })),
          ...LATER_SECTIONS.map((s) => ({
            key: s.key,
            icon: s.icon,
            label: (
              <span
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                {s.label}
                <Tag
                  variant="filled"
                  style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px', opacity: 0.8 }}
                >
                  later
                </Tag>
              </span>
            ),
          })),
        ]}
      />
    </Sider>
    <Layout>
      <Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 12,
          padding: '0 24px',
          borderBottom: '1px solid #e8e9f3',
        }}
      >
        <EnvBadge />
        <Typography.Text type="secondary" ellipsis style={{ maxWidth: 240 }}>
          {email}
        </Typography.Text>
        <Button icon={<LogoutOutlined />} onClick={onSignOut}>
          Sign out
        </Button>
      </Header>
      {/* The library and user lists have many columns and use the room a wide screen has. */}
      <Content
        style={{
          padding: 24,
          maxWidth: WIDE_ROUTES.includes(route.name) ? 1680 : 1100,
          width: '100%',
        }}
      >
        {children}
      </Content>
    </Layout>
  </Layout>
);
